import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Button, Input, Modal, Form, Select, Switch, Space,
  Card, message, Tooltip, Popconfirm, Tag, InputNumber, Tabs, Badge,
} from 'antd';
import {
  SettingOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
  HomeOutlined, UnorderedListOutlined, TeamOutlined,
  UserOutlined, IdcardOutlined, LockOutlined, SearchOutlined,
  CarOutlined, WarningOutlined, BankOutlined,
} from '@ant-design/icons';
import { AutoComplete } from 'antd';
import axiosInstance from '../utils/axios';
import { hssColors, commonStyles } from '../config/theme';
import Footer from '../components/Footer';

const { Option } = Select;

// ── Checklist config ──────────────────────────────────────────────────────────
const TIPO_V_LABEL = { auto: 'Auto', camion: 'Camión', moto: 'Moto' };
const TIPO_V_COLOR = { auto: '#3498db', camion: '#e67e22', moto: '#e74c3c' };
const CAT_COLOR = {
  bateria: 'gold', luces: 'blue', exterior: 'orange',
  tablero: 'purple', interior: 'green', equipamiento: 'red',
};
const CATEGORIAS = ['bateria', 'luces', 'exterior', 'tablero', 'interior', 'equipamiento'];

// ── Checklist tab ─────────────────────────────────────────────────────────────
const ChecklistTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tipoActivo, setTipoActivo] = useState('auto');
  const [modal, setModal] = useState({ open: false, modo: 'nuevo', item: null });
  const [form] = Form.useForm();

  const cargar = async () => {
    setLoading(true);
    try { setItems((await axiosInstance.get('/checklist/plantilla')).data); }
    catch { message.error('Error al cargar plantilla'); }
    finally { setLoading(false); }
  };
  useEffect(() => { cargar(); }, []);

  const abrirNuevo = () => {
    form.resetFields();
    form.setFieldsValue({ activo: true, tipo_vehiculo: tipoActivo, categoria: 'luces' });
    setModal({ open: true, modo: 'nuevo', item: null });
  };
  const abrirEditar = (item) => { form.setFieldsValue(item); setModal({ open: true, modo: 'editar', item }); };
  const cerrar = () => { setModal(m => ({ ...m, open: false })); form.resetFields(); };

  const onSubmit = async (values) => {
    try {
      if (modal.modo === 'nuevo') await axiosInstance.post('/checklist/plantilla', values);
      else await axiosInstance.put(`/checklist/plantilla/${modal.item.id}`, values);
      message.success(modal.modo === 'nuevo' ? 'Ítem agregado' : 'Ítem actualizado');
      cerrar(); cargar();
    } catch (e) { message.error(e.response?.data?.error || 'Error'); }
  };

  const onEliminar = async (id) => {
    try { await axiosInstance.delete(`/checklist/plantilla/${id}`); message.success('Eliminado'); cargar(); }
    catch (e) { message.error(e.response?.data?.error || 'Error'); }
  };

  const onToggle = async (item, activo) => {
    try {
      await axiosInstance.put(`/checklist/plantilla/${item.id}`, { ...item, activo });
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, activo } : i));
    } catch { message.error('Error'); }
  };

  const columns = [
    { title: '#', dataIndex: 'orden', width: 50, render: v => <span style={{ fontFamily: 'monospace', fontSize: 12, color: hssColors.textSecondary }}>{v}</span> },
    { title: 'Categoría', dataIndex: 'categoria', width: 130,
      render: c => <Tag color={CAT_COLOR[c] || 'default'} style={{ textTransform: 'capitalize' }}>{c}</Tag>,
      filters: CATEGORIAS.map(c => ({ text: c.charAt(0).toUpperCase() + c.slice(1), value: c })),
      onFilter: (v, r) => r.categoria === v,
    },
    { title: 'Descripción', dataIndex: 'descripcion', render: d => <span style={{ fontWeight: 500 }}>{d}</span> },
    { title: 'Activo', dataIndex: 'activo', width: 75,
      render: (activo, r) => <Switch checked={activo} size="small" onChange={v => onToggle(r, v)} />,
    },
    { title: '', key: 'acc', width: 85,
      render: (_, r) => (
        <Space>
          <Tooltip title="Editar"><Button type="text" icon={<EditOutlined />} style={{ color: hssColors.primary }} onClick={() => abrirEditar(r)} /></Tooltip>
          <Popconfirm title="¿Eliminar?" onConfirm={() => onEliminar(r.id)} okText="Sí" cancelText="No">
            <Button type="text" icon={<DeleteOutlined />} style={{ color: hssColors.error }} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const subtabs = Object.entries(TIPO_V_LABEL).map(([tipo, label]) => {
    const lista = items.filter(i => i.tipo_vehiculo === tipo);
    return {
      key: tipo,
      label: <Space><span style={{ color: TIPO_V_COLOR[tipo], fontWeight: 700 }}>{label}</span>
        <Badge count={lista.filter(i => i.activo).length} style={{ background: TIPO_V_COLOR[tipo], fontSize: 11 }} /></Space>,
      children: <Table columns={columns} dataSource={lista} rowKey="id" loading={loading}
        scroll={{ x: 'max-content' }} pagination={false} size="middle" rowClassName={r => !r.activo ? 'row-inactive' : ''} />,
    };
  });

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={abrirNuevo} style={{ borderRadius: 8, fontWeight: 600 }}>
          Nuevo ítem — {TIPO_V_LABEL[tipoActivo]}
        </Button>
      </div>
      <Tabs activeKey={tipoActivo} onChange={setTipoActivo} items={subtabs} />

      <Modal
        title={<Space style={{ fontSize: 17, fontWeight: 700, color: hssColors.primary }}><UnorderedListOutlined /><span>{modal.modo === 'nuevo' ? 'Nuevo ítem' : 'Editar ítem'}</span></Space>}
        open={modal.open} onCancel={cerrar} footer={null} width={460}
      >
        <Form form={form} onFinish={onSubmit} layout="vertical" style={{ marginTop: 20 }}>
          <Form.Item name="tipo_vehiculo" label="Tipo de vehículo" rules={[{ required: true }]}>
            <Select size="large">
              {Object.entries(TIPO_V_LABEL).map(([v, l]) => <Option key={v} value={v}><span style={{ color: TIPO_V_COLOR[v], fontWeight: 700 }}>{l}</span></Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="categoria" label="Categoría" rules={[{ required: true }]}>
            <Select size="large">
              {CATEGORIAS.map(c => <Option key={c} value={c}><Tag color={CAT_COLOR[c]}>{c.charAt(0).toUpperCase() + c.slice(1)}</Tag></Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="descripcion" label="Descripción" rules={[{ required: true, message: 'Requerido' }]}>
            <Input size="large" style={{ borderRadius: 8 }} placeholder="Ej: Luz de Freno..." />
          </Form.Item>
          <Form.Item name="orden" label="Orden"><InputNumber size="large" style={{ width: '100%', borderRadius: 8 }} min={0} /></Form.Item>
          <Form.Item name="activo" label="Activo" valuePropName="checked"><Switch /></Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={cerrar} size="large" style={{ borderRadius: 8 }}>Cancelar</Button>
              <Button type="primary" htmlType="submit" size="large" style={{ borderRadius: 8, fontWeight: 600 }}>
                {modal.modo === 'nuevo' ? 'Agregar' : 'Guardar'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// ── Usuarios tab ──────────────────────────────────────────────────────────────
const UsuariosTab = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [modal, setModal] = useState({ open: false, modo: 'nuevo', usuario: null });
  const [search, setSearch] = useState('');
  const [form] = Form.useForm();

  const cargar = async () => {
    try { setUsuarios((await axiosInstance.get('/usuarios')).data); }
    catch { message.error('Error al cargar usuarios'); }
  };
  useEffect(() => { cargar(); }, []);

  const abrirNuevo = () => {
    form.resetFields();
    setModal({ open: true, modo: 'nuevo', usuario: null });
  };

  const abrirEditar = (u) => {
    form.setFieldsValue({ dni: u.dni, nombre: u.nombre, usuario: u.usuario, rol: u.rol, contrasena: '' });
    setModal({ open: true, modo: 'editar', usuario: u });
  };

  const cerrar = () => { setModal({ open: false, modo: 'nuevo', usuario: null }); form.resetFields(); };

  const onFinish = async (values) => {
    try {
      if (modal.modo === 'nuevo') {
        await axiosInstance.post('/usuarios', values);
        message.success('Usuario creado');
      } else {
        const payload = { nombre: values.nombre, usuario: values.usuario, rol: values.rol };
        if (values.contrasena) payload.contrasena = values.contrasena;
        await axiosInstance.put(`/usuarios/${modal.usuario.dni}`, payload);
        message.success('Usuario actualizado');
      }
      cerrar();
      cargar();
    } catch (e) { message.error(e.response?.data?.error || 'Error al guardar'); }
  };

  const eliminar = async (u) => {
    try {
      await axiosInstance.delete(`/usuarios/${u.dni}`);
      message.success('Usuario eliminado');
      cargar();
    } catch (e) { message.error(e.response?.data?.error || 'Error al eliminar'); }
  };

  const filtrados = usuarios.filter(u => {
    if (!search) return true;
    const s = search.toLowerCase();
    return u.dni?.toString().includes(s) || u.nombre?.toLowerCase().includes(s) || u.usuario?.toLowerCase().includes(s);
  });

  const columns = [
    { title: 'CI', dataIndex: 'dni', width: 110,
      render: v => <Space><IdcardOutlined style={{ color: hssColors.primary }} /><span style={{ fontWeight: 600 }}>{v}</span></Space> },
    { title: 'Nombre', dataIndex: 'nombre',
      render: v => <Space><UserOutlined style={{ color: hssColors.success }} /><span style={{ fontWeight: 500 }}>{v}</span></Space> },
    { title: 'Usuario', dataIndex: 'usuario',
      render: v => <span style={{ padding: '3px 10px', background: hssColors.gray100, borderRadius: 6, fontFamily: 'monospace', fontWeight: 600, color: hssColors.primary }}>{v}</span> },
    { title: 'Rol', dataIndex: 'rol',
      render: r => <Tag color={r === 'admin' ? 'gold' : 'blue'}>{r === 'admin' ? 'Administrador' : 'Usuario'}</Tag> },
    { title: 'Acciones', key: 'acciones', width: 100, align: 'center',
      render: (_, u) => (
        <Space>
          <Tooltip title="Editar">
            <Button type="text" icon={<EditOutlined />} style={{ color: hssColors.primary }} onClick={() => abrirEditar(u)} />
          </Tooltip>
          <Tooltip title="Eliminar">
            <Popconfirm
              title={`¿Eliminar al usuario "${u.usuario}"?`}
              description="Esta acción no se puede deshacer."
              onConfirm={() => eliminar(u)}
              okText="Eliminar" cancelText="Cancelar"
              okButtonProps={{ danger: true }}
            >
              <Button type="text" icon={<DeleteOutlined />} danger />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Input placeholder="Buscar por nombre, CI o usuario..."
          prefix={<SearchOutlined style={{ color: hssColors.gray400 }} />}
          style={{ width: 360, borderRadius: 8 }} size="large"
          value={search} onChange={e => setSearch(e.target.value)} allowClear />
        <Button type="primary" icon={<PlusOutlined />} size="large"
          onClick={abrirNuevo} style={{ borderRadius: 8, fontWeight: 600 }}>
          Nuevo usuario
        </Button>
      </div>
      <Table columns={columns} dataSource={filtrados} rowKey="dni"
        scroll={{ x: 'max-content' }} pagination={{ pageSize: 10, showTotal: t => `Total: ${t} usuarios` }} size="middle" />

      <Modal
        title={<Space style={{ fontSize: 18, fontWeight: 700, color: hssColors.primary }}><TeamOutlined />
          <span>{modal.modo === 'nuevo' ? 'Nuevo Usuario' : `Editar: ${modal.usuario?.nombre}`}</span>
        </Space>}
        open={modal.open} onCancel={cerrar} footer={null} width={520}
      >
        <Form form={form} onFinish={onFinish} layout="vertical" style={{ marginTop: 20 }}>
          <Form.Item name="dni" label="CI" rules={[{ required: modal.modo === 'nuevo' }]}>
            <Input prefix={<IdcardOutlined style={{ color: hssColors.gray400 }} />} type="number" size="large"
              style={{ borderRadius: 8 }} placeholder="Número de CI" disabled={modal.modo === 'editar'} />
          </Form.Item>
          <Form.Item name="nombre" label="Nombre completo" rules={[{ required: true }]}>
            <Input prefix={<UserOutlined style={{ color: hssColors.gray400 }} />} size="large" style={{ borderRadius: 8 }} />
          </Form.Item>
          <Form.Item name="usuario" label="Nombre de usuario" rules={[{ required: true }]}>
            <Input prefix={<UserOutlined style={{ color: hssColors.gray400 }} />} size="large" style={{ borderRadius: 8 }} placeholder="Para el login" />
          </Form.Item>
          <Form.Item
            name="contrasena"
            label={modal.modo === 'editar' ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña'}
            rules={modal.modo === 'nuevo' ? [{ required: true }, { min: 6, message: 'Mínimo 6 caracteres' }] : [{ min: 6, message: 'Mínimo 6 caracteres', warningOnly: false }]}
          >
            <Input.Password prefix={<LockOutlined style={{ color: hssColors.gray400 }} />} size="large" style={{ borderRadius: 8 }} />
          </Form.Item>
          <Form.Item name="rol" label="Rol" initialValue="administrativo" rules={[{ required: true }]}>
            <Select size="large">
              <Option value="admin">Administrador</Option>
              <Option value="administrativo">Administrativo</Option>
              <Option value="tecnico">Técnico</Option>
            </Select>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={cerrar} size="large" style={{ borderRadius: 8 }}>Cancelar</Button>
              <Button type="primary" htmlType="submit" size="large" style={{ borderRadius: 8, fontWeight: 600 }}>
                {modal.modo === 'nuevo' ? 'Crear usuario' : 'Guardar cambios'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// ── Identificadores tab ───────────────────────────────────────────────────────
const IdentificadoresTab = () => {
  const [vehiculos, setVehiculos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [busqV, setBusqV] = useState('');
  const [busqC, setBusqC] = useState('');
  const [selV, setSelV] = useState(null);
  const [selC, setSelC] = useState(null);
  const [nuevaChapa, setNuevaChapa] = useState('');
  const [nuevoDni, setNuevoDni] = useState('');
  const [loadingV, setLoadingV] = useState(false);
  const [loadingC, setLoadingC] = useState(false);

  useEffect(() => {
    axiosInstance.get('/vehiculos').then(r => setVehiculos(r.data)).catch(() => {});
    axiosInstance.get('/clientes').then(r => setClientes(r.data)).catch(() => {});
  }, []);

  const optsV = vehiculos
    .filter(v => !busqV || v.chapa.toLowerCase().includes(busqV.toLowerCase()) || `${v.marca} ${v.modelo}`.toLowerCase().includes(busqV.toLowerCase()))
    .slice(0, 30)
    .map(v => ({ value: v.chapa, label: `${v.chapa} — ${v.marca} ${v.modelo}` }));

  const optsC = clientes
    .filter(c => !busqC || c.dni?.toString().includes(busqC) || `${c.nombre} ${c.apellido || ''}`.toLowerCase().includes(busqC.toLowerCase()))
    .slice(0, 30)
    .map(c => ({ value: c.dni?.toString(), label: `${c.dni} — ${c.nombre} ${c.apellido || ''}` }));

  const renombrarChapa = async () => {
    if (!selV || !nuevaChapa.trim()) return;
    setLoadingV(true);
    try {
      const r = await axiosInstance.put(`/vehiculos/${selV.chapa}/renombrar`, { nueva_chapa: nuevaChapa.trim() });
      message.success(r.data.mensaje);
      setSelV(null); setBusqV(''); setNuevaChapa('');
      const res = await axiosInstance.get('/vehiculos');
      setVehiculos(res.data);
    } catch (e) {
      message.error(e.response?.data?.error || 'Error al renombrar');
    } finally { setLoadingV(false); }
  };

  const renombrarDni = async () => {
    if (!selC || !nuevoDni.trim()) return;
    const nd = nuevoDni.trim();
    if (!/^\d{4,10}(-\d)?$/.test(nd)) { message.error('El CI/RUC debe tener solo dígitos, o dígitos seguidos de un guion y el dígito verificador (ej. 80108372-9)'); return; }
    setLoadingC(true);
    try {
      const r = await axiosInstance.put(`/clientes/${selC.dni}/renombrar`, { nuevo_dni: nd });
      message.success(r.data.mensaje);
      setSelC(null); setBusqC(''); setNuevoDni('');
      const res = await axiosInstance.get('/clientes');
      setClientes(res.data);
    } catch (e) {
      message.error(e.response?.data?.error || 'Error al actualizar');
    } finally { setLoadingC(false); }
  };

  const cardStyle = { borderRadius: 10, border: `1px solid ${hssColors.gray200}`, marginBottom: 0 };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={24}>
      <div style={{ padding: '12px 16px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 8, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <WarningOutlined style={{ color: '#faad14', marginTop: 2 }} />
        <span style={{ fontSize: 13, color: '#7c5a00' }}>
          Esta sección modifica la clave primaria. El sistema actualiza automáticamente todas las relaciones (vehículos, contratos, órdenes, etc.). Usá con cuidado.
        </span>
      </div>

      {/* Renombrar chapa */}
      <Card title={<Space><CarOutlined style={{ color: hssColors.secondary }} /><span style={{ fontWeight: 700 }}>Renombrar Chapa de Vehículo</span></Space>} style={cardStyle}>
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <AutoComplete
            style={{ width: '100%' }}
            size="large"
            options={optsV}
            value={busqV}
            onSearch={v => { setBusqV(v); setSelV(null); setNuevaChapa(''); }}
            onSelect={(val, opt) => {
              const v = vehiculos.find(x => x.chapa === val);
              setSelV(v); setBusqV(opt.label); setNuevaChapa('');
            }}
            placeholder="Buscar por chapa o vehículo..."
            allowClear
            onClear={() => { setSelV(null); setBusqV(''); setNuevaChapa(''); }}
          />
          {selV && (
            <div style={{ padding: '10px 14px', background: hssColors.gray100, borderRadius: 8, fontSize: 13 }}>
              <strong>Chapa actual:</strong> <code style={{ fontSize: 14, fontWeight: 700 }}>{selV.chapa}</code>
              {' · '}{selV.marca} {selV.modelo}
            </div>
          )}
          <Input
            size="large"
            placeholder="Nueva chapa"
            value={nuevaChapa}
            onChange={e => setNuevaChapa(e.target.value.toUpperCase())}
            disabled={!selV}
            style={{ borderRadius: 8, fontWeight: 600, fontFamily: 'monospace', letterSpacing: 1 }}
            prefix={<CarOutlined style={{ color: hssColors.gray400 }} />}
          />
          <Popconfirm
            title={`¿Renombrar "${selV?.chapa}" → "${nuevaChapa}"?`}
            description="Se actualizarán todas las órdenes, GPS, contratos y accesos vinculados."
            onConfirm={renombrarChapa}
            okText="Sí, renombrar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
            disabled={!selV || !nuevaChapa.trim() || nuevaChapa.trim() === selV?.chapa}
          >
            <Button
              type="primary" danger size="large"
              loading={loadingV}
              disabled={!selV || !nuevaChapa.trim() || nuevaChapa.trim() === selV?.chapa}
              style={{ borderRadius: 8, fontWeight: 600 }}
            >
              Renombrar chapa
            </Button>
          </Popconfirm>
        </Space>
      </Card>

      {/* Cambiar CI/RUC */}
      <Card title={<Space><IdcardOutlined style={{ color: hssColors.primary }} /><span style={{ fontWeight: 700 }}>Cambiar CI / RUC de Cliente</span></Space>} style={cardStyle}>
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <AutoComplete
            style={{ width: '100%' }}
            size="large"
            options={optsC}
            value={busqC}
            onSearch={v => { setBusqC(v); setSelC(null); setNuevoDni(''); }}
            onSelect={(val, opt) => {
              const c = clientes.find(x => x.dni?.toString() === val);
              setSelC(c); setBusqC(opt.label); setNuevoDni('');
            }}
            placeholder="Buscar por CI / RUC o nombre..."
            allowClear
            onClear={() => { setSelC(null); setBusqC(''); setNuevoDni(''); }}
          />
          {selC && (
            <div style={{ padding: '10px 14px', background: hssColors.gray100, borderRadius: 8, fontSize: 13 }}>
              <Tag color={selC.tipo_cliente === 'EMPRESA' ? 'purple' : 'blue'} icon={selC.tipo_cliente === 'EMPRESA' ? <BankOutlined /> : <IdcardOutlined />}>
                {selC.tipo_cliente === 'EMPRESA' ? 'Empresa' : 'Persona'}
              </Tag>
              <strong style={{ marginLeft: 8 }}>{selC.tipo_cliente === 'EMPRESA' ? 'RUC' : 'CI'} actual:</strong>{' '}
              <code style={{ fontSize: 14, fontWeight: 700 }}>{selC.dni}</code>
              {' · '}{selC.nombre} {selC.apellido || ''}
            </div>
          )}
          <Input
            size="large"
            placeholder={selC?.tipo_cliente === 'EMPRESA' ? 'Nuevo RUC (ej. 80108372-9)' : 'Nuevo número de CI'}
            value={nuevoDni}
            onChange={e => setNuevoDni(e.target.value.replace(/[^0-9-]/g, ''))}
            disabled={!selC}
            style={{ borderRadius: 8, fontWeight: 600, fontFamily: 'monospace' }}
            prefix={<IdcardOutlined style={{ color: hssColors.gray400 }} />}
          />
          <Popconfirm
            title={`¿Cambiar ${selC?.tipo_cliente === 'EMPRESA' ? 'RUC' : 'CI'} ${selC?.dni} → ${nuevoDni}?`}
            description="Se actualizarán todos los vehículos, contratos y accesos vinculados."
            onConfirm={renombrarDni}
            okText="Sí, actualizar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
            disabled={!selC || !nuevoDni || nuevoDni === selC?.dni?.toString()}
          >
            <Button
              type="primary" danger size="large"
              loading={loadingC}
              disabled={!selC || !nuevoDni || nuevoDni === selC?.dni?.toString()}
              style={{ borderRadius: 8, fontWeight: 600 }}
            >
              Actualizar CI / RUC
            </Button>
          </Popconfirm>
        </Space>
      </Card>
    </Space>
  );
};

// ── Settings page ─────────────────────────────────────────────────────────────
const Settings = () => {
  const navigate = useNavigate();
  const [tabPrincipal, setTabPrincipal] = useState('checklist');

  const tabs = [
    {
      key: 'checklist',
      label: <Space><UnorderedListOutlined />Checklist</Space>,
      children: <ChecklistTab />,
    },
    {
      key: 'usuarios',
      label: <Space><TeamOutlined />Usuarios</Space>,
      children: <UsuariosTab />,
    },
    {
      key: 'identificadores',
      label: <Space><IdcardOutlined />Identificadores</Space>,
      children: <IdentificadoresTab />,
    },
  ];

  return (
    <div style={{ minHeight: '100vh', background: hssColors.backgroundPrimary, display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: hssColors.backgroundSecondary, borderBottom: `1px solid ${hssColors.gray200}`, padding: '16px 32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <SettingOutlined style={{ fontSize: 28, color: hssColors.primary }} />
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: hssColors.primary }}>Configuración</h1>
              <p style={{ margin: 0, fontSize: 13, color: hssColors.textSecondary }}>Checklist · Usuarios · Identificadores</p>
            </div>
          </div>
          <Button icon={<HomeOutlined />} onClick={() => navigate('/dashboard')} size="large" style={{ height: 44, borderRadius: 8 }}>
            Inicio
          </Button>
        </div>
      </div>

      <div style={{ padding: '24px 32px', flex: 1 }}>
        <Card style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}>
          <Tabs activeKey={tabPrincipal} onChange={setTabPrincipal} items={tabs} size="large" />
        </Card>
      </div>

      <style>{`.row-inactive td { opacity: 0.4; }`}</style>
      <Footer />
    </div>
  );
};

export default Settings;
